/**
 * EXIF-Daten vom Original in das verkleinerte Foto übernehmen.
 *
 * Ein Bild, das über ein `canvas` neu gezeichnet wird, besteht nur noch aus
 * Bildpunkten — Aufnahmezeit, Kamera und **Ort** sind weg. Für einen
 * Reisebericht ist gerade der Ort die Angabe, die man später sucht. Also wird
 * der EXIF-Block aus der Originaldatei herausgeschnitten und in das neue JPEG
 * gesetzt.
 *
 * Ohne Fremdbibliothek, weil auch das Frontend ohne Abhängigkeiten auskommen
 * soll, die keiner mehr pflegt. Gebraucht wird davon ohnehin nur ein kleiner
 * Teil: den APP1-Block finden, eine einzige Marke darin korrigieren, den Block
 * wieder einsetzen.
 *
 * Aufbau einer JPEG-Datei: `FFD8` (Anfang), dann Segmente aus `FF`, Kennung,
 * Länge (zwei Byte, die Länge zählt sich selbst mit) und Inhalt. Der
 * EXIF-Block ist das Segment `FFE1`, dessen Inhalt mit `Exif\0\0` beginnt.
 */

const SOI = 0xd8
const SOS = 0xda
const APP1 = 0xe1

export function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length > 3 && bytes[0] === 0xff && bytes[1] === SOI
}

/** Sucht den EXIF-Block und gibt ihn samt Kennung und Länge zurück. */
export function findExif(jpeg: Uint8Array): Uint8Array | null {
  if (!isJpeg(jpeg)) return null

  let at = 2
  while (at + 3 < jpeg.length) {
    if (jpeg[at] !== 0xff) return null

    const marker = jpeg[at + 1]
    // Ab hier kommen die Bildpunkte; danach steht kein EXIF mehr.
    if (marker === SOS) return null

    const hoch = jpeg[at + 2]
    const niedrig = jpeg[at + 3]
    if (hoch === undefined || niedrig === undefined) return null

    const length = (hoch << 8) | niedrig
    if (length < 2) return null

    if (marker === APP1 && startsWithExif(jpeg, at + 4)) {
      return jpeg.subarray(at, at + 2 + length)
    }
    at += 2 + length
  }
  return null
}

function startsWithExif(bytes: Uint8Array, at: number): boolean {
  return (
    bytes[at] === 0x45 && // E
    bytes[at + 1] === 0x78 && // x
    bytes[at + 2] === 0x69 && // i
    bytes[at + 3] === 0x66 && // f
    bytes[at + 4] === 0x00 &&
    bytes[at + 5] === 0x00
  )
}

/**
 * Setzt die Ausrichtung auf „aufrecht".
 *
 * Das Bild wurde beim Verkleinern schon gedreht — `createImageBitmap` richtet
 * es nach der EXIF-Marke aus. Übernähme man diese Marke unverändert, drehte
 * der Betrachter ein zweites Mal, und das Foto läge quer. Alles andere,
 * besonders die GPS-Angaben, bleibt unangetastet.
 */
export function uprightExif(segment: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(segment)
  // 2 Byte Kennung + 2 Byte Länge + 6 Byte "Exif\0\0" = Anfang des TIFF-Kopfs.
  const tiff = 10
  if (copy.length < tiff + 8) return copy

  const little = copy[tiff] === 0x49 && copy[tiff + 1] === 0x49
  const view = new DataView(copy.buffer, copy.byteOffset, copy.byteLength)

  // Der TIFF-Kopf verweist auf das erste Verzeichnis (IFD0).
  const ifd0 = tiff + view.getUint32(tiff + 4, little)
  if (ifd0 + 2 > copy.length) return copy

  const entries = view.getUint16(ifd0, little)
  for (let index = 0; index < entries; index++) {
    const entry = ifd0 + 2 + index * 12
    if (entry + 12 > copy.length) break

    // 0x0112 ist die Ausrichtung. Sie steht als SHORT unmittelbar im Eintrag.
    if (view.getUint16(entry, little) === 0x0112) {
      view.setUint16(entry + 8, 1, little)
      break
    }
  }
  return copy
}

/**
 * Setzt einen EXIF-Block in ein JPEG ein.
 *
 * Der Block gehört unmittelbar hinter den Dateianfang. Ein bereits
 * vorhandener EXIF-Block im Ziel wird ersetzt — der Browser schreibt zwar
 * keinen, aber verlassen sollte man sich darauf nicht.
 */
export function withExif(jpeg: Uint8Array, exif: Uint8Array): Uint8Array<ArrayBuffer> {
  if (!isJpeg(jpeg)) return new Uint8Array(jpeg)

  const vorhanden = findExif(jpeg)
  const rest = vorhanden
    ? concat(
        jpeg.subarray(2, vorhanden.byteOffset - jpeg.byteOffset),
        jpeg.subarray(vorhanden.byteOffset - jpeg.byteOffset + vorhanden.length),
      )
    : jpeg.subarray(2)

  return concat(new Uint8Array([0xff, SOI]), exif, rest)
}

function concat(...teile: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const gesamt = teile.reduce((summe, teil) => summe + teil.length, 0)
  const ergebnis = new Uint8Array(new ArrayBuffer(gesamt))
  let at = 0
  for (const teil of teile) {
    ergebnis.set(teil, at)
    at += teil.length
  }
  return ergebnis
}
